import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./SearchableDropdown.css";

const getOptionValue = (option, valueKey) => {
  if (!option) return "";
  if (typeof option !== "object") return option;

  return (
    option[valueKey] ??
    option.value ??
    option.id ??
    option._id ??
    option.name ??
    ""
  );
};

const getOptionLabel = (option, labelKey, valueKey) => {
  if (!option) return "";
  if (typeof option !== "object") return String(option);

  return String(
    option[labelKey] ??
      option.label ??
      option.name ??
      option.title ??
      option.account_name ??
      option.accountName ??
      option.customer_name ??
      option.customerName ??
      option.vendor_name ??
      option.vendorName ??
      option.item_name ??
      option.itemName ??
      option.product_name ??
      option.productName ??
      option.category_name ??
      option.categoryName ??
      option.bank_name ??
      option.bankName ??
      option.payment_mode ??
      option.paymentMode ??
      getOptionValue(option, valueKey) ??
      ""
  );
};

const normalizeOptions = (options, labelKey, valueKey) => {
  if (!Array.isArray(options)) return [];

  return options.flatMap((option) => {
    if (option?.options && Array.isArray(option.options)) {
      return normalizeOptions(option.options, labelKey, valueKey);
    }

    if (typeof option !== "object") {
      return {
        label: String(option),
        value: option,
      };
    }

    return {
      ...option,
      label: getOptionLabel(option, labelKey, valueKey),
      value: getOptionValue(option, valueKey),
    };
  });
};

const getComparableValue = (value, valueKey) => {
  if (value && typeof value === "object") {
    return getOptionValue(value, valueKey);
  }

  return value;
};

const SALUTATION_VALUES = ["Mr.", "Mrs.", "Ms.", "Miss.", "Dr."];

const isSalutationDropdown = (options = []) => {
  if (!Array.isArray(options) || options.length === 0) return false;

  const enabledValues = options
    .filter((option) => !option.disabled)
    .map((option) => String(option.value ?? option.label ?? ""));

  return (
    enabledValues.length > 0 &&
    enabledValues.every((value) => SALUTATION_VALUES.includes(value))
  );
};

const SearchableDropdown = ({
  options = [],
  children,
  labelKey = "label",
  valueKey = "value",
  value = "",
  selectedLabel = "",
  name,
  onChange,
  onSelect,
  className,
  class: classProp,
  placeholder = "Search",
  disabled = false,
  isDisabled = false,
  required = false,
  style,
  styles,
  components: selectComponents,
  menuPortalTarget,
  menuPosition,
  classNamePrefix,
  defaultValue,
  isClearable,
  isSearchable,
  isMulti,
  ...props
}) => {
  const [searchText, setSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState(null);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const generatedListboxId = useRef(
    `search-dropdown-${Math.random().toString(36).slice(2)}`
  );
  const listboxId = name
    ? `search-dropdown-${String(name).replace(/[^a-zA-Z0-9_-]/g, "-")}`
    : generatedListboxId.current;

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedControl =
        dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedMenu =
        menuRef.current && menuRef.current.contains(event.target);

      if (!clickedControl && !clickedMenu) {
        setIsOpen(false);
        setIsTyping(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const childOptions = useMemo(
    () =>
      React.Children.toArray(children)
        .filter((child) => React.isValidElement(child))
        .map((child) => {
          const childValue =
            child.props.value !== undefined
              ? child.props.value
              : child.props.children;

          return {
            label: React.Children.toArray(child.props.children).join(""),
            value: childValue,
            disabled: child.props.disabled,
          };
        }),
    [children]
  );

  const normalizedOptions = useMemo(
    () =>
      options.length > 0
        ? normalizeOptions(options, labelKey, valueKey)
        : childOptions,
    [childOptions, labelKey, options, valueKey]
  );

  const resolvedName = name || (isSalutationDropdown(normalizedOptions) ? "someName" : undefined);

  const selectedOption = useMemo(
    () =>
      normalizedOptions.find((item) => {
        const comparableValue = String(getComparableValue(value, valueKey) ?? "");
        return (
          String(item.value ?? "") === comparableValue ||
          String(item.label ?? "") === comparableValue
        );
      }),
    [normalizedOptions, value, valueKey]
  );

  const resolvedSelectedLabel =
    selectedLabel ||
    String(
      selectedOption?.label ||
        getOptionLabel(defaultValue, labelKey, valueKey) ||
        ""
    );

  const displayText = isTyping ? searchText : resolvedSelectedLabel;
  const inputClassName = className || classProp || "form-control dropdown-field";
  const legacyContainerStyle =
    typeof styles?.container === "function" ? styles.container({}, {}) : {};
  const legacyControlStyle =
    typeof styles?.control === "function" ? styles.control({}, {}) : {};
  const wrapperStyle = {
    ...legacyContainerStyle,
    width: legacyContainerStyle.width || legacyControlStyle.width,
    marginTop: legacyContainerStyle.marginTop || legacyControlStyle.marginTop,
    marginRight: legacyContainerStyle.marginRight || legacyControlStyle.marginRight,
    marginBottom: legacyContainerStyle.marginBottom || legacyControlStyle.marginBottom,
    marginLeft: legacyContainerStyle.marginLeft || legacyControlStyle.marginLeft,
    position: "relative",
  };
  const inputStyle = {
    ...legacyControlStyle,
    width: "100%",
    marginTop: undefined,
    marginRight: undefined,
    marginBottom: undefined,
    marginLeft: undefined,
    paddingRight: legacyControlStyle.paddingRight || "44px",
    ...style,
  };

  const filteredOptions = isTyping
    ? normalizedOptions.filter((item) =>
        String(item.label || "")
          .toLowerCase()
          .includes(searchText.toLowerCase())
      )
    : normalizedOptions;

  useEffect(() => {
    setActiveIndex(0);
  }, [filteredOptions.length, isOpen, searchText]);

  const selectOption = useCallback(
    (item) => {
      if (!item || item.disabled) {
        return;
      }

      if (onSelect) {
        onSelect(item);
      }

      if (onChange) {
        onChange({
          ...item,
          name: resolvedName,
          target: {
            name: resolvedName,
            value: item.value,
          },
        });
      }

      setSearchText("");
      setIsTyping(false);
      setIsOpen(false);
    },
    [onChange, onSelect, resolvedName]
  );

  const clearSelection = useCallback(() => {
    if (onChange) {
      onChange({
        name: resolvedName,
        value: "",
        target: {
          name: resolvedName,
          value: "",
        },
      });
    }

    setSearchText("");
    setIsTyping(false);
    setIsOpen(false);
  }, [onChange, resolvedName]);

  const updateMenuPosition = useCallback(() => {
    if (!dropdownRef.current || typeof window === "undefined") return;

    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportPadding = 12;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openAbove = spaceBelow < 140 && spaceAbove > spaceBelow;
    const availableSpace = openAbove ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(120, Math.min(260, availableSpace - gap));

    const menuWidth =
      name === "tax"
        ? Math.max(rect.width, 160)
        : rect.width;

    setMenuStyle({
      left: rect.left,
      top: openAbove
        ? Math.max(viewportPadding, rect.top - maxHeight - gap)
        : Math.min(rect.bottom + gap, window.innerHeight - viewportPadding),
      width: menuWidth,
      maxHeight,
    });
  }, [name]);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [displayText, filteredOptions.length, isOpen, updateMenuPosition]);

  const handleOpen = () => {
    setIsOpen(true);
    setIsTyping(false);
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(updateMenuPosition);
    } else {
      updateMenuPosition();
    }
  };

  const dropdownMenu =
    isOpen && menuStyle ? (
      <ul
        id={listboxId}
        role="listbox"
        className="search-dropdown-list search-dropdown-list-portal"
        ref={menuRef}
        style={menuStyle}
        onMouseDown={(event) => event.preventDefault()}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((item, index) => (
            <li
              key={String(item.value ?? item.label)}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={String(item.value ?? "") === String(selectedOption?.value ?? "")}
              className={[
                item.disabled ? "search-dropdown-disabled" : "",
                index === activeIndex ? "search-dropdown-active" : "",
                String(item.value ?? "") === String(selectedOption?.value ?? "")
                  ? "search-dropdown-selected"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                selectOption(item);
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                selectOption(item);
              }}
            >
              {item.label}
            </li>
          ))
        ) : (
          <li className="search-dropdown-empty">No results found</li>
        )}
      </ul>
    ) : null;

  return (
    <div className="search-dropdown-wrapper" ref={dropdownRef} style={wrapperStyle}>
      <input
        {...props}
        type="text"
        name={resolvedName}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={
          isOpen && filteredOptions.length > 0
            ? `${listboxId}-option-${activeIndex}`
            : undefined
        }
        className={inputClassName}
        value={displayText}
        placeholder={placeholder}
        disabled={disabled || isDisabled}
        required={required}
        style={inputStyle}
        onFocus={handleOpen}
        onClick={handleOpen}
        onChange={(e) => {
          setSearchText(e.target.value);
          setIsTyping(true);
          setIsOpen(true);
        }}
        onKeyDown={(event) => {
          if (props.onKeyDown) {
            props.onKeyDown(event);
          }

          if (event.defaultPrevented) return;
          if (disabled || isDisabled) return;

          if (event.key === "ArrowDown") {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((current) =>
              filteredOptions.length
                ? Math.min(current + 1, filteredOptions.length - 1)
                : 0
            );
          }

          if (event.key === "ArrowUp") {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((current) => Math.max(current - 1, 0));
          }

          if (event.key === "Enter" && isOpen) {
            event.preventDefault();
            selectOption(filteredOptions[activeIndex]);
          }

          if (event.key === "Escape") {
            setSearchText("");
            setIsTyping(false);
            setIsOpen(false);
          }

          if (event.key === "Home" && isOpen && filteredOptions.length) {
            event.preventDefault();
            setActiveIndex(0);
          }

          if (event.key === "End" && isOpen && filteredOptions.length) {
            event.preventDefault();
            setActiveIndex(filteredOptions.length - 1);
          }

          if (
            event.key === "Backspace" &&
            isClearable &&
            !isTyping &&
            resolvedSelectedLabel
          ) {
            clearSelection();
          }
        }}
      />
      {isClearable && resolvedSelectedLabel && !(disabled || isDisabled) ? (
        <button
          type="button"
          className="search-dropdown-clear"
          aria-label="Clear selected option"
          onMouseDown={(event) => {
            event.preventDefault();
            clearSelection();
          }}
        >
          ×
        </button>
      ) : null}
      <span className="search-dropdown-arrow" aria-hidden="true">▾</span>
      {dropdownMenu && typeof document !== "undefined"
        ? createPortal(dropdownMenu, document.body)
        : dropdownMenu}
    </div>
  );
};

export default SearchableDropdown;
export const components = {
  Control: ({ children, innerProps, className, ...props }) => (
    <div {...innerProps} className={className} {...props}>
      {children}
    </div>
  ),
};
